import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DriverActivityStatus, DriverInformation } from 'app/models';

type OperatorMarkerKind = 'idle' | 'onTrip';

const STATUS_COLORS: Record<OperatorMarkerKind, string> = {
	idle: '#f59e0b',
	onTrip: '#3b82f6'
};

const STATUS_ICONS: Record<OperatorMarkerKind, string> = {
	idle: 'schedule',
	onTrip: 'local_taxi'
};

const VEHICLE_ICON_URL = '/XpressApp/assets/images/live-map-icons/service-types/sedan.svg';

interface OperatorVehiclePillOverlayData {
	driverId: number;
	position: google.maps.LatLng;
	label: string;
	markerKind: OperatorMarkerKind;
	vehicleIconUrl: string;
	isSelected: boolean;
	onClick: () => void;
}

interface OperatorVehiclePillOverlayInstance extends google.maps.OverlayView {
	getDriverId(): number;
}

type OperatorVehiclePillOverlayCtor = new (
	data: OperatorVehiclePillOverlayData
) => OperatorVehiclePillOverlayInstance;

let operatorVehiclePillOverlayCtor: OperatorVehiclePillOverlayCtor | null = null;

const applySaPillInlineStyles = (
	root: HTMLElement,
	hit: HTMLElement,
	pillWrap: HTMLElement,
	pill: HTMLElement,
	statusBadge: HTMLElement,
	statusIcon: HTMLElement,
	badge: HTMLElement,
	vehicleImg: HTMLImageElement,
	label: HTMLElement,
	arrow: HTMLElement,
	halo: HTMLElement | null,
	color: string,
	isSelected: boolean
): void => {
	root.style.position = 'absolute';
	root.style.transform = 'translate(-50%, -100%)';
	root.style.pointerEvents = 'none';
	root.style.zIndex = isSelected ? '10' : '1';

	hit.style.display = 'flex';
	hit.style.flexDirection = 'column';
	hit.style.alignItems = 'center';
	hit.style.overflow = 'visible';
	hit.style.pointerEvents = 'auto';
	hit.style.cursor = 'pointer';

	pillWrap.style.position = 'relative';
	pillWrap.style.display = 'flex';
	pillWrap.style.alignItems = 'center';
	pillWrap.style.justifyContent = 'center';

	if (halo) {
		halo.style.position = 'absolute';
		halo.style.top = '50%';
		halo.style.left = '50%';
		halo.style.width = 'calc(100% + 1rem)';
		halo.style.height = 'calc(100% + 0.65rem)';
		halo.style.transform = 'translate(-50%, -50%)';
		halo.style.borderRadius = '2.5rem';
		halo.style.border = '2px solid rgba(45, 72, 55, 0.55)';
		halo.style.background = 'rgba(148, 163, 184, 0.28)';
		halo.style.pointerEvents = 'none';
		halo.style.zIndex = '0';
		halo.style.boxSizing = 'border-box';
	}

	pill.style.position = 'relative';
	pill.style.zIndex = '1';
	pill.style.overflow = 'visible';
	pill.style.display = 'flex';
	pill.style.alignItems = 'center';
	pill.style.gap = '0.5rem';
	pill.style.padding = '0.35rem 0.75rem 0.35rem 0.35rem';
	pill.style.background = '#ffffff';
	pill.style.border = `2.5px solid ${color}`;
	pill.style.borderRadius = '2rem';
	pill.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
	pill.style.whiteSpace = 'nowrap';

	statusBadge.style.position = 'absolute';
	statusBadge.style.top = '-0.4rem';
	statusBadge.style.left = '-0.4rem';
	statusBadge.style.width = '1.25rem';
	statusBadge.style.height = '1.25rem';
	statusBadge.style.borderRadius = '50%';
	statusBadge.style.display = 'flex';
	statusBadge.style.alignItems = 'center';
	statusBadge.style.justifyContent = 'center';
	statusBadge.style.zIndex = '2';
	statusBadge.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.3)';
	statusBadge.style.backgroundColor = color;

	statusIcon.style.fontSize = '0.75rem';
	statusIcon.style.width = '0.75rem';
	statusIcon.style.height = '0.75rem';
	statusIcon.style.lineHeight = '1';
	statusIcon.style.color = '#fff';

	badge.style.width = '2rem';
	badge.style.height = '2rem';
	badge.style.borderRadius = '50%';
	badge.style.background = '#f1f5f9';
	badge.style.display = 'flex';
	badge.style.alignItems = 'center';
	badge.style.justifyContent = 'center';
	badge.style.flexShrink = '0';
	badge.style.overflow = 'hidden';

	vehicleImg.style.width = '1.5rem';
	vehicleImg.style.height = '1.5rem';
	vehicleImg.style.objectFit = 'contain';

	label.style.fontSize = '0.75rem';
	label.style.fontWeight = '700';
	label.style.color = '#1e293b';
	label.style.letterSpacing = '0.03em';
	label.style.maxWidth = '9rem';
	label.style.overflow = 'hidden';
	label.style.textOverflow = 'ellipsis';

	arrow.style.width = '0';
	arrow.style.height = '0';
	arrow.style.borderLeft = '7px solid transparent';
	arrow.style.borderRight = '7px solid transparent';
	arrow.style.borderTop = `8px solid ${color}`;
	arrow.style.marginTop = '-1px';
};

const getOperatorVehiclePillOverlayCtor = (): OperatorVehiclePillOverlayCtor => {
	if (operatorVehiclePillOverlayCtor) {
		return operatorVehiclePillOverlayCtor;
	}

	const g = (window as unknown as { google?: typeof google }).google;
	if (!g?.maps?.OverlayView) {
		throw new Error('Google Maps API is not loaded yet.');
	}

	operatorVehiclePillOverlayCtor = class OperatorVehiclePillOverlay
		extends g.maps.OverlayView
		implements OperatorVehiclePillOverlayInstance
	{
		private readonly data: OperatorVehiclePillOverlayData;
		private element: HTMLElement | null = null;

		constructor(data: OperatorVehiclePillOverlayData) {
			super();
			this.data = data;
		}

		override onAdd(): void {
			const color = STATUS_COLORS[this.data.markerKind];
			const iconName = STATUS_ICONS[this.data.markerKind];

			const root = document.createElement('div');
			root.className = 'live-map-vehicle-pill';
			if (this.data.isSelected) {
				root.classList.add('live-map-vehicle-pill--selected');
			}

			const hit = document.createElement('div');
			hit.className = 'live-map-vehicle-pill__hit';
			hit.addEventListener('click', () => this.data.onClick());

			const pillWrap = document.createElement('div');
			pillWrap.className = 'live-map-vehicle-pill__pill-wrap';

			let halo: HTMLElement | null = null;
			if (this.data.isSelected) {
				halo = document.createElement('div');
				halo.className = 'live-map-vehicle-pill__halo';
				pillWrap.appendChild(halo);
			}

			const pill = document.createElement('div');
			pill.className = 'live-map-vehicle-pill__body';

			const statusBadge = document.createElement('div');
			statusBadge.className = 'live-map-vehicle-pill__status-badge';

			const statusIcon = document.createElement('span');
			statusIcon.className = 'material-icons live-map-vehicle-pill__status-icon';
			statusIcon.textContent = iconName;
			statusBadge.appendChild(statusIcon);
			pill.appendChild(statusBadge);

			const badge = document.createElement('div');
			badge.className = 'live-map-vehicle-pill__badge';

			const vehicleImg = document.createElement('img');
			vehicleImg.className = 'live-map-vehicle-pill__badge-icon';
			vehicleImg.src = this.data.vehicleIconUrl;
			vehicleImg.alt = '';
			badge.appendChild(vehicleImg);
			pill.appendChild(badge);

			const label = document.createElement('span');
			label.className = 'live-map-vehicle-pill__plate';
			label.textContent = this.data.label || '—';
			pill.appendChild(label);

			pillWrap.appendChild(pill);
			hit.appendChild(pillWrap);

			const arrow = document.createElement('div');
			arrow.className = 'live-map-vehicle-pill__arrow';
			hit.appendChild(arrow);

			root.appendChild(hit);
			this.element = root;

			applySaPillInlineStyles(
				root,
				hit,
				pillWrap,
				pill,
				statusBadge,
				statusIcon,
				badge,
				vehicleImg,
				label,
				arrow,
				halo,
				color,
				this.data.isSelected
			);

			const panes = this.getPanes();
			if (panes?.overlayMouseTarget) {
				panes.overlayMouseTarget.appendChild(root);
			}
		}

		override draw(): void {
			if (!this.element) {
				return;
			}

			const projection = this.getProjection();
			if (!projection) {
				return;
			}
			const point = projection.fromLatLngToDivPixel(this.data.position);
			if (!point) {
				return;
			}

			this.element.style.left = `${point.x}px`;
			this.element.style.top = `${point.y}px`;
		}

		override onRemove(): void {
			if (this.element?.parentNode) {
				this.element.parentNode.removeChild(this.element);
			}
			this.element = null;
		}

		getDriverId(): number {
			return this.data.driverId;
		}
	};

	return operatorVehiclePillOverlayCtor;
};

@Injectable()
export class OperatorLiveMapMarkersService implements OnDestroy {
	private pillOverlays: OperatorVehiclePillOverlayInstance[] = [];
	private currentMap: google.maps.Map | null = null;
	private currentDrivers: DriverInformation[] = [];
	private selectedDriverId: number | null = null;
	private hiddenDriverId: number | null = null;

	private readonly markerClickSubject = new Subject<DriverInformation>();
	public readonly markerClick$: Observable<DriverInformation> = this.markerClickSubject.asObservable();

	constructor(private ngZone: NgZone) {}

	public setDrivers(
		drivers: DriverInformation[],
		map: google.maps.Map,
		options?: { hiddenDriverId?: number | null }
	): void {
		this.currentDrivers = drivers ?? [];
		this.currentMap = map;
		if (options && 'hiddenDriverId' in options) {
			this.hiddenDriverId = options.hiddenDriverId ?? null;
		}
		this.renderMarkers();
	}

	public setHiddenDriverId(driverId: number | null): void {
		if (this.hiddenDriverId === driverId) {
			return;
		}
		this.hiddenDriverId = driverId;
		this.renderMarkers();
	}

	public setSelectedDriverId(driverId: number | null): void {
		if (this.selectedDriverId === driverId) {
			return;
		}
		this.selectedDriverId = driverId;
		this.renderMarkers();
	}

	public getVisibleMarkerPositions(): google.maps.LatLngLiteral[] {
		const positions: google.maps.LatLngLiteral[] = [];
		for (const driver of this.currentDrivers) {
			if (this.hiddenDriverId != null && driver.DriverId === this.hiddenDriverId) {
				continue;
			}
			const coords = this.getDriverCoords(driver);
			if (coords) {
				positions.push(coords);
			}
		}
		return positions;
	}

	public clearMarkers(): void {
		this.clearPillOverlays();
		this.currentDrivers = [];
		this.currentMap = null;
		this.selectedDriverId = null;
		this.hiddenDriverId = null;
	}

	public ngOnDestroy(): void {
		this.clearMarkers();
		this.markerClickSubject.complete();
	}

	private renderMarkers(): void {
		if (!this.currentMap) {
			return;
		}

		this.clearPillOverlays();

		const OverlayCtor = getOperatorVehiclePillOverlayCtor();

		for (const driver of this.currentDrivers) {
			if (this.hiddenDriverId != null && driver.DriverId === this.hiddenDriverId) {
				continue;
			}

			const coords = this.getDriverCoords(driver);
			if (!coords) {
				continue;
			}

			const markerKind = this.getMarkerKind(driver.DriverActivityStatus);
			const position = new google.maps.LatLng(coords.lat, coords.lng);
			const label = this.getDriverDisplayName(driver);
			const isSelected = this.selectedDriverId === driver.DriverId;

			const overlay = new OverlayCtor({
				driverId: driver.DriverId,
				position,
				label,
				markerKind,
				vehicleIconUrl: VEHICLE_ICON_URL,
				isSelected,
				onClick: () => this.ngZone.run(() => this.markerClickSubject.next(driver))
			});
			overlay.setMap(this.currentMap);
			this.pillOverlays.push(overlay);
		}
	}

	private clearPillOverlays(): void {
		for (const overlay of this.pillOverlays) {
			overlay.setMap(null);
		}
		this.pillOverlays = [];
	}

	private getMarkerKind(status: DriverActivityStatus): OperatorMarkerKind {
		return status === DriverActivityStatus.OnTrip ? 'onTrip' : 'idle';
	}

	private getDriverDisplayName(driver: DriverInformation): string {
		const name = [driver.FirstName, driver.LastName].filter(Boolean).join(' ').trim();
		return name || '—';
	}

	private getDriverCoords(driver: DriverInformation): google.maps.LatLngLiteral | null {
		const lat = Number(driver.Lat);
		const lng = Number(driver.Long);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			return null;
		}
		if (lat === 0 && lng === 0) {
			return null;
		}
		return { lat, lng };
	}
}
